import { useChain } from '@cosmos-kit/react';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useDelegations, useValidators } from '@/hooks/useStaking';
import type { ValidatorInfo, DelegationResponse, UnbondingDelegation, Balance } from '@/types/staking';
import {
  formatTokenWithSymbol,
  shortenAddress,
  formatTimeRemaining,
} from '@/utils/formatting';
import {
  shouldShowRedelegateFromInactive,
  calculateRedelegationFromInactive,
  isValidatorActive,
  getExcludedTopValidatorAddresses,
  isSignificantExcludedDelegation,
  type RedelegationPlan,
} from '@/utils/redelegation';
import { EXCLUDED_TOP_VALIDATORS_COUNT } from '@/config/constants';
import { Loader, Table, type Column, Button, CopyableAddress } from '@/components/common';

// Modal component for detailed rewards breakdown
function RewardsModal({ rewards, onClose }: { rewards: Balance[]; onClose: () => void }): JSX.Element {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return (): void => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return (): void => { document.removeEventListener('keydown', handleEscape); };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl p-4 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Rewards Breakdown</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>
        {rewards.length === 0 ? (
          <p className="text-gray-500">No pending rewards</p>
        ) : (
          <div className="space-y-2">
            {rewards.map((r) => (
              <div key={r.denom} className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                <span className="text-gray-600 truncate max-w-[200px]" title={r.denom}>
                  {r.symbol}
                </span>
                <span className="font-medium text-gray-900 ml-4 whitespace-nowrap">
                  {r.amount_display}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Clickable rewards cell that shows a modal with breakdown
function RewardsCell({ rewards, displayAmount }: { rewards: Balance[]; displayAmount: string }): JSX.Element {
  const [showModal, setShowModal] = useState(false);
  const hasMultipleTokens = rewards.length > 1;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowModal(!showModal)}
        className={`text-left ${hasMultipleTokens ? 'text-blue-600 hover:text-blue-800 cursor-pointer underline' : ''}`}
        title={hasMultipleTokens ? 'Click to see all reward tokens' : undefined}
      >
        {displayAmount}
        {hasMultipleTokens && <span className="ml-1 text-xs">+{rewards.length - 1}</span>}
      </button>
      {showModal && (
        <RewardsModal rewards={rewards} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

interface MyDelegationsProps {
  onUndelegate: (validator: ValidatorInfo, delegation: DelegationResponse) => void;
  onRedelegate: (validator: ValidatorInfo, delegation: DelegationResponse) => void;
  onClaimRewards: (validator: ValidatorInfo) => void;
  onClaimAllRewards: (validators: ValidatorInfo[]) => void;
  onRedelegateFromInactive: (plan: RedelegationPlan, validatorMap: Map<string, ValidatorInfo>) => void;
}

export function MyDelegations({
  onUndelegate,
  onRedelegate,
  onClaimRewards,
  onClaimAllRewards,
  onRedelegateFromInactive,
}: MyDelegationsProps): JSX.Element {
  const { address } = useChain('gravitybridge');
  const { data: delegatorData, isLoading } = useDelegations(address ?? undefined);
  const { data: validators } = useValidators();
  const [hideTinyDelegations, setHideTinyDelegations] = useState(true);

  // Create a map of validator addresses to their info for quick lookup
  const validatorMap = new Map<string, ValidatorInfo>();
  if (validators !== undefined && Array.isArray(validators)) {
    for (const validator of validators) {
      if (validator.operator_address) {
        validatorMap.set(validator.operator_address, validator);
      }
    }
  }

  // Helper to get validator name from address
  const getValidatorName = (operatorAddress: string): string => {
    const validator = validatorMap.get(operatorAddress);
    return validator?.description?.moniker ?? shortenAddress(operatorAddress);
  };

  // Helper to check if validator is in a problematic state (not active)
  // Uses the shared isValidatorActive from redelegation.ts
  const isValidatorProblematic = (operatorAddress: string): boolean => {
    const validator = validatorMap.get(operatorAddress);
    if (validator === undefined) return false;
    return !isValidatorActive(validator);
  };

  // Compute excluded top-validator set once
  const excludedSet = useMemo(
    () => getExcludedTopValidatorAddresses(validators ?? []),
    [validators],
  );

  if (address === undefined || address === null || address === '') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please connect your wallet to view your delegations.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader size="lg" />
      </div>
    );
  }

  const delegations: DelegationResponse[] = delegatorData?.delegations ?? [];
  const unbondingDelegations: UnbondingDelegation[] = delegatorData?.unbonding_delegations ?? [];
  const rewards = delegatorData?.rewards;

  // Get total GRAV rewards from the total array (ugraviton denom)
  const totalGravRewards: number = ((): number => {
    const ugravitonTotal = (rewards?.total ?? []).find(
      (coin: { denom: string; amount: string }) => coin.denom === 'ugraviton'
    );
    if (ugravitonTotal === undefined) return 0;
    const amount = parseFloat(ugravitonTotal.amount);
    return isNaN(amount) ? 0 : amount / 1e6; // Convert ugraviton to GRAV
  })();

  // Get all non-GRAV reward tokens
  const otherRewardTokens: Balance[] = (rewards?.total ?? []).filter(
    (coin: Balance) => coin.denom !== 'ugraviton' && parseFloat(coin.amount) > 0
  );

  // Total rewards for display: use the actual total from backend
  const totalRewards = totalGravRewards;

  // Handler for claiming all rewards
  const handleClaimAllRewards = (): void => {
    // Get all validators that have any rewards > 0, excluding excluded-top validators
    // with a significant delegation (those must be claimed outside this interface).
    const validatorsWithRewards = (rewards?.rewards ?? [])
      .filter((r) => {
        // Check if any reward token has a non-zero amount
        return r.reward?.some((coin) => parseFloat(coin.amount) > 0) ?? false;
      })
      .filter((r) => {
        const balance = delegations.find((d) => d.delegation.validator_address === r.validator_address)?.balance.amount;
        return !(excludedSet.has(r.validator_address) && balance !== undefined && isSignificantExcludedDelegation(balance));
      })
      .map((r) => validatorMap.get(r.validator_address))
      .filter((v): v is ValidatorInfo => v !== undefined);

    if (validatorsWithRewards.length > 0) {
      onClaimAllRewards(validatorsWithRewards);
    }
  };

  // Filter delegations for redelegation calculations if hiding tiny delegations
  const delegationsForRedelegation = hideTinyDelegations
    ? delegations.filter((delegation) => {
        const validator = validatorMap.get(delegation.delegation.validator_address);
        if (validator === undefined) return true;
        // If validator is active (bonded and not jailed), always include
        if (!validator.jailed && validator.status === 3) return true;
        // If validator is inactive, only include if amount >= 1 GRAV
        const amountNum = parseFloat(delegation.balance.amount) / 1e6;
        return amountNum >= 1;
      })
    : delegations;

  // Check if we should show the "Redelegate from inactive" button
  const showRedelegateFromInactive = shouldShowRedelegateFromInactive(delegationsForRedelegation, validatorMap);

  // Handler for redelegating from inactive validators
  const handleRedelegateFromInactive = (): void => {
    console.log(
      '[redelegation-debug] handleRedelegateFromInactive: hideTinyDelegations=', hideTinyDelegations,
      'delegations.length=', delegations.length,
      'delegationsForRedelegation.length=', delegationsForRedelegation.length,
    );
    const plan = calculateRedelegationFromInactive(delegationsForRedelegation, validatorMap);
    console.log('[redelegation-debug] handleRedelegateFromInactive: plan.operations.length=', plan.operations.length);
    if (plan.operations.length > 0) {
      onRedelegateFromInactive(plan, validatorMap);
    }
  };

  // Check if the user has any significant delegation to an excluded top validator
  const hasSignificantExcludedDelegation = delegations.some((d) => {
    return excludedSet.has(d.delegation.validator_address)
      && isSignificantExcludedDelegation(d.balance.amount);
  });

  // Whether there are any claimable rewards left once excluded-top-validator
  // rewards (which must be claimed outside this interface) are set aside.
  const claimableRewardsExist = (rewards?.rewards ?? []).some((r) => {
    const hasReward = r.reward?.some((coin) => parseFloat(coin.amount) > 0) ?? false;
    if (!hasReward) return false;
    const balance = delegations.find((d) => d.delegation.validator_address === r.validator_address)?.balance.amount;
    const isExcludedSig = excludedSet.has(r.validator_address) && balance !== undefined && isSignificantExcludedDelegation(balance);
    return !isExcludedSig;
  });

  type DelegationRow = {
    id: string;
    validator_address: string;
    validator_name: string;
    amount: string;
    rewards: string;
    rewardsRaw: number;
    rewardsArray: Balance[];
    delegation: DelegationResponse;
    isProblematic: boolean;
    /** Validator is in the excluded top-N and the delegation is significant */
    isExcludedSignificant: boolean;
  };

  const delegationColumns: Column<DelegationRow>[] = [
    { 
      key: 'validator', 
      header: 'Validator',
      render: (row) => (
        <div>
          <div className="font-medium text-sm">
            {row.validator_name}
          </div>
          <CopyableAddress address={row.validator_address} />
        </div>
      )
    },
    { key: 'amount', header: 'Amount', render: (row) => row.amount },
    { 
      key: 'rewards', 
      header: 'Rewards', 
      render: (row) => (
        <RewardsCell rewards={row.rewardsArray} displayAmount={row.rewards} />
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (row): JSX.Element => {
        if (row.isExcludedSignificant) {
          return <span className="text-yellow-700 text-xs">⚠️ Top validator — excluded</span>;
        }
        if (row.isProblematic) {
          return <span className="text-red-600 text-xs">⚠️ Not active - redelegate to earn rewards</span>;
        }
        return <span className="text-green-600 text-xs">✓ Active</span>;
      }
    },
    { 
      key: 'actions', 
      header: '',
      render: (row): JSX.Element => {
        const validator = validatorMap.get(row.validator_address);
        return (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (validator !== undefined) {
                  onClaimRewards(validator);
                }
              }}
              disabled={row.rewardsRaw === 0 || validator === undefined || row.isExcludedSignificant}
            >
              Claim
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (validator !== undefined) {
                  onRedelegate(validator, row.delegation);
                }
              }}
              disabled={validator === undefined}
            >
              Redelegate
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (validator !== undefined) {
                  onUndelegate(validator, row.delegation);
                }
              }}
              disabled={validator === undefined}
            >
              Undelegate
            </Button>
          </div>
        );
      }
    },
  ];

  const delegationRows: DelegationRow[] = delegations.map((delegation: DelegationResponse) => {
    const validatorRewards = (rewards?.rewards ?? []).find(
      (r: { validator_address: string; reward: Array<{ amount: string; denom: string }> }) => r.validator_address === delegation.delegation.validator_address
    );
    const rewardsArray: Balance[] = validatorRewards?.reward ?? [];
    // Find ugraviton reward specifically for the GRAV display
    const ugravitonReward = rewardsArray.find((r) => r.denom === 'ugraviton');
    const rewardAmountRaw: string = ugravitonReward?.amount ?? '0';
    const rewardAmountNum = parseFloat(rewardAmountRaw) / 1e6; // Convert ugraviton to GRAV

    return {
      id: delegation.delegation.validator_address,
      validator_address: delegation.delegation.validator_address,
      validator_name: getValidatorName(delegation.delegation.validator_address),
      amount: formatTokenWithSymbol(delegation.balance.amount),
      rewards: formatTokenWithSymbol(rewardAmountRaw),
      rewardsRaw: rewardAmountNum,
      rewardsArray,
      delegation,
      isProblematic: isValidatorProblematic(delegation.delegation.validator_address),
      isExcludedSignificant: excludedSet.has(delegation.delegation.validator_address)
        && isSignificantExcludedDelegation(delegation.balance.amount),
    };
  });

  // Filter out tiny delegations to inactive validators if the option is enabled
  const filteredDelegationRows = hideTinyDelegations
    ? delegationRows.filter((row) => {
        // If the validator is not problematic, always show
        if (!row.isProblematic) return true;
        // If the validator is problematic, only show if amount >= 1 GRAV
        // Parse the amount (format is like "123.456789 GRAV")
        const amountStr = row.delegation.balance.amount;
        const amountNum = parseFloat(amountStr) / 1e6; // Convert from ugraviton to GRAV
        return amountNum >= 1;
      })
    : delegationRows;

  const unbondingColumns: Column<UnbondingDelegation>[] = [
    { 
      key: 'validator', 
      header: 'Validator',
      render: (unbonding) => (
        <div>
          <div className="text-sm font-medium">
            {getValidatorName(unbonding.validator_address)}
          </div>
          <div className="text-xs text-gray-500">
            {shortenAddress(unbonding.validator_address)}
          </div>
        </div>
      )
    },
    { 
      key: 'amount', 
      header: 'Amount',
      render: (unbonding) => formatTokenWithSymbol(unbonding.balance)
    },
    { 
      key: 'completion', 
      header: 'Completion Time',
      render: (unbonding) => formatTimeRemaining(unbonding.completion_time)
    },
  ];

  return (
    <div className="space-y-8">
      {/* Active Delegations */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-black">Active Delegations</h2>
          {delegations.length > 0 && (
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="text-gray-500">Total Rewards: </span>
                <span className="font-medium text-gray-900">{totalRewards.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} GRAV</span>
                {otherRewardTokens.length > 0 && (
                  <span className="ml-1 text-blue-600">+{otherRewardTokens.length} more</span>
                )}
              </div>
              {showRedelegateFromInactive && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRedelegateFromInactive}
                  className="bg-orange-500 hover:bg-orange-600 text-white border border-orange-600"
                >
                  ⚠️ Redelegate from Inactive
                </Button>
              )}
              {(totalRewards > 0 || otherRewardTokens.length > 0) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleClaimAllRewards}
                  disabled={!claimableRewardsExist}
                  title={
                    !claimableRewardsExist
                      ? 'Redelegate away from excluded top validators first'
                      : hasSignificantExcludedDelegation
                        ? 'Rewards from excluded top validators will be skipped; claim the rest outside this interface'
                        : undefined
                  }
                >
                  Claim All Rewards
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Hide tiny delegations toggle */}
        {delegations.length > 0 && (
          <div className="mb-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={hideTinyDelegations}
                onChange={(e) => setHideTinyDelegations(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span>Hide tiny delegations (&lt; 1 GRAV) to inactive validators</span>
            </label>
          </div>
        )}

        {/* Top-validator exclusion alarm banner */}
        {hasSignificantExcludedDelegation && (
          <div className="mb-4 rounded-md border border-yellow-400 bg-yellow-50 p-4 text-sm text-yellow-800">
            <p className="font-semibold mb-1">⚠️ Top Validator Delegation Detected</p>
            <p>
              The top {EXCLUDED_TOP_VALIDATORS_COUNT} validators are excluded from staking or claiming via this
              interface. To continue you must redelegate from these validators using the redelegate button. You can
              continue to manage your stake to these validators via the CLI or another application. The
              decentralization of the Gravity Bridge protocol requires a decentralized validator set and it is
              important to spread stake across multiple validators and avoid voting power concentration.
            </p>
          </div>
        )}

        {delegations.length === 0 ? (
          <div className="text-center py-8 text-gray-600">
            No active delegations. Delegate to a validator to start earning rewards.
          </div>
        ) : (
          <Table 
            columns={delegationColumns} 
            data={filteredDelegationRows} 
            keyExtractor={(row) => row.id}
            rowClassName={(row) => {
              if (row.isExcludedSignificant) return 'bg-yellow-50';
              if (row.isProblematic) return 'bg-red-50';
              return '';
            }}
          />
        )}
      </div>

      {/* Unbonding Delegations */}
      {unbondingDelegations.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Unbonding Delegations</h2>
          <Table 
            columns={unbondingColumns} 
            data={unbondingDelegations} 
            keyExtractor={(u) => `${u.validator_address}-${u.creation_height}`} 
          />
        </div>
      )}
    </div>
  );
}
